/**
 * Scanner-burst detection (design/ux/01_foundations.md §4.8, WP-17).
 *
 * Fed with keydown events (key + timeStamp), it tells a wedge-scanner burst
 * from human typing: ≥ `minLength` printable characters whose every gap is
 * ≤ `maxGapMs`, ended by a terminator (Entrée / Tab). A slow key restarts the
 * buffer from that key, so human typing — however long — is never a scan,
 * and a scan that starts right after typing is still recognised.
 *
 * Pure (no DOM): hooks/useScanInput.js wires it to a field or to the window;
 * the per-workstation preferences are read and written here with a guarded
 * storage (a private window or blocked site data never breaks scanning).
 */
import {
  SCAN_BEEP_DEFAULT,
  SCAN_GAP_OPTIONS,
  SCAN_MAX_INTER_KEY_MS,
  SCAN_MIN_LENGTH,
  SCAN_TERMINATORS,
  SCANNER_PREFS_STORAGE_KEY,
} from "../config/scanner.config.js";

/** A single printable character a scanner can emit (GS1 group separator included). */
function isScanChar(e) {
  if (e.ctrlKey || e.altKey || e.metaKey) return false;
  return typeof e.key === "string" && e.key.length === 1;
}

/**
 * @param {{ minLength?: number, maxGapMs?: number, terminators?: ReadonlyArray<string> }} [opts]
 */
export function createScanDetector(opts = {}) {
  const minLength = opts.minLength ?? SCAN_MIN_LENGTH;
  const maxGapMs = opts.maxGapMs ?? SCAN_MAX_INTER_KEY_MS;
  const terminators = opts.terminators ?? SCAN_TERMINATORS;
  let chars = [];
  let last = -Infinity;

  const reset = () => {
    chars = [];
    last = -Infinity;
  };

  return {
    /**
     * @param {{ key: string, timeStamp: number, ctrlKey?: boolean, altKey?: boolean, metaKey?: boolean, shiftKey?: boolean }} e
     * @returns {null | { type: "scan", code: string }}
     */
    feed(e) {
      const t = Number(e.timeStamp) || 0;
      // A scanner never auto-repeats: a held key (a held « + » on a grid row,
      // a held digit) arrives every ~30 ms and must never pass for a burst.
      if (e.repeat) {
        reset();
        return null;
      }
      if (terminators.includes(e.key)) {
        const fast = chars.length > 0 && t - last <= maxGapMs;
        const code = chars.join("");
        reset();
        if (fast && code.length >= minLength) return { type: "scan", code };
        return null;
      }
      if (e.key === "Shift") return null; // scanners send Maj for capitals
      if (!isScanChar(e)) {
        reset();
        return null;
      }
      if (chars.length > 0 && t - last > maxGapMs) chars = [];
      chars.push(e.key);
      last = t;
      return null;
    },
    /** Characters of the burst in progress (tests / debugging). */
    pending: () => chars.join(""),
    /** True while a fast burst is being received. */
    inBurst: (now) => chars.length > 1 && now - last <= maxGapMs,
    reset,
  };
}

// ── Per-workstation preferences (§4.8 « a per-workstation setting ») ────────

const ALLOWED_GAPS = new Set(SCAN_GAP_OPTIONS.map((o) => o.value));

/** @returns {{ beep: boolean, maxGapMs: number }} */
export function readScannerPrefs(storage = globalThis.localStorage) {
  const fallback = { beep: SCAN_BEEP_DEFAULT, maxGapMs: SCAN_MAX_INTER_KEY_MS };
  try {
    const raw = storage?.getItem(SCANNER_PREFS_STORAGE_KEY);
    if (!raw) return fallback;
    const v = JSON.parse(raw);
    return {
      beep: typeof v?.beep === "boolean" ? v.beep : fallback.beep,
      maxGapMs: ALLOWED_GAPS.has(v?.maxGapMs) ? v.maxGapMs : fallback.maxGapMs,
    };
  } catch {
    return fallback;
  }
}

/** Merge and persist; returns the preferences now in effect (kept for this page when storage is blocked). */
export function writeScannerPrefs(patch, storage = globalThis.localStorage) {
  const next = { ...readScannerPrefs(storage), ...patch };
  if (!ALLOWED_GAPS.has(next.maxGapMs)) next.maxGapMs = SCAN_MAX_INTER_KEY_MS;
  next.beep = Boolean(next.beep);
  try {
    storage?.setItem(SCANNER_PREFS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* blocked storage: the preference lives for this page only */
  }
  return next;
}
